import React, { useState, useEffect, useRef, useCallback } from 'react'
import _ from 'lodash'
import moment from 'moment'
import { Icon, PopupTooltip, constants } from '@adc/ui-core'
import { DateUtils } from 'react-day-picker'
import DayPickerInput from 'react-day-picker/DayPickerInput'
import MomentLocaleUtils from 'react-day-picker/moment'
import 'react-day-picker/lib/style.css'
import classnames from 'classnames/bind'
import styles from './styles.scss'
const cx = classnames.bind(styles)

const DATEPICKER_TOOLTIP =
  'You can query before and after the selected day by clicking on the same day consecutively.'

const { UI_DATE_FORMAT, UI_DATE_FORMAT_RANGE } = constants.statics
const allDatesBefore = (dateStr: string) => _.startsWith(_.trim(dateStr), '<')
const allDatesAfter = (dateStr: string) => _.startsWith(_.trim(dateStr), '>')
const stripDateBeforeAfter = (dateStr: string) => {
  if (allDatesAfter(dateStr) || allDatesBefore(dateStr)) {
    return normalizeDate(dateStr).substring(1)
  } else return dateStr
}

const isRangeQuery = (dateStr: string) => {
  const re = /^(\d{2}\/\d{2}\/\d{4})-(\d{2}\/\d{2}\/\d{4})$/
  const match = normalizeDate(dateStr).match(re)

  if (match && new Date(match[1]) < new Date(match[2])) {
    return match
  } else {
    return null
  }
}

/**
 * Check if it's a range query and return object used to capture selected dates.
 */
const parseRangeQuery = (dateStr: string) => {
  const rangeQuery = isRangeQuery(dateStr)

  return {
    from: rangeQuery ? rangeQuery[1] : dateStr,
    to: rangeQuery ? rangeQuery[2] : null,
  }
}

/**
 * Removes whitespace from strings.
 */
const normalizeDate = (input: string): string =>
  typeof input === 'string' ? input.replace(/\s/g, '') : input

interface DatePickerProps {
  getRef: (ref: DayPickerInput | null) => void
  searchRef: { state: { value: string } }
  updateClause: (
    e: { target: { value: string } },
    onUpdate?: () => void
  ) => void
  searchValue?: string
  submit: (e: React.KeyboardEvent | { type: string }) => void
  onInputChange?: (e: { target: { value: string } }) => void
  onClear?: () => void
}

interface DateRange {
  from: Date | null
  to: Date | null
}

interface OverlayProps {
  onBlur?: React.FocusEventHandler
  onFocus?: React.FocusEventHandler
  children: React.ReactNode
  classNames: {
    overlayWrapper: string
    overlay: string
    container: string
  }
}

/**
 * DatePicker is a wrapper around the DayPickerPackage we are using. It passes in an
 * overlay for additional functionality. Parts of it needed so decent hacking to play
 * well with all of the requested functionality, mainly the ability to do date range
 * searches. The goal is to eventually replace this with a date range picker which ironically
 * seems a bit more straightforward to implement.
 */
const DatePicker: React.FC<DatePickerProps> = ({
  getRef,
  searchRef,
  updateClause,
  searchValue = '',
  submit,
  onInputChange = _.noop,
  onClear = _.noop,
}) => {
  const [beforeAfterRange, setBeforeAfterRange] = useState('')
  const [before, setBefore] = useState<Date | null>(null)
  const [after, setAfter] = useState<Date | null>(null)
  const [range, setRange] = useState<DateRange>({ from: null, to: null })

  const prevSearchValueRef = useRef<string>(searchValue)

  const isValidDate = useCallback(
    (dateStr: string, format: string) =>
      moment(dateStr, format, true).isValid(),
    []
  )

  const formatDate = useCallback(
    (date: Date, format: string) => {
      return beforeAfterRange + moment(date).format(format)
    },
    [beforeAfterRange]
  )

  const convertToNoonDate = useCallback((dateStr: string, format: string) => {
    // Convert date to match with what react-day-picker outputs
    return moment(dateStr, format).add(12, 'hours').toDate()
  }, [])

  const resetBeforeAfter = useCallback(() => {
    setBeforeAfterRange('')
    setBefore(null)
    setAfter(null)
  }, [])

  const resetSelectedDate = useCallback(() => {
    setBeforeAfterRange('')
    setBefore(null)
    setAfter(null)
    setRange({ from: null, to: null })
  }, [])

  const findDatepickerQuery = useCallback(
    (val: string) => {
      const rangeQuery = parseRangeQuery(val)

      let datepickerQuery = ''
      if (isValidDate(stripDateBeforeAfter(rangeQuery.from), UI_DATE_FORMAT)) {
        datepickerQuery += rangeQuery.from
      } else {
        return ''
      }
      if (
        rangeQuery.to &&
        isValidDate(stripDateBeforeAfter(rangeQuery.to), UI_DATE_FORMAT)
      ) {
        datepickerQuery += ' - ' + rangeQuery.to
      }

      return datepickerQuery
    },
    [isValidDate]
  )

  const updateClauseInternal = useCallback(
    (val: string | moment.Moment, onUpdate?: () => void) => {
      // Make sure value is in UI date format
      let formattedVal: string
      if (moment.isMoment(val)) {
        formattedVal = beforeAfterRange + moment(val).format(UI_DATE_FORMAT)
      } else {
        formattedVal = val
      }

      updateClause(
        { target: { value: findDatepickerQuery(formattedVal) } },
        onUpdate
      )
    },
    [beforeAfterRange, findDatepickerQuery, updateClause]
  )

  /**
   * Logic to check if query is for a range, before, after, or single day
   * Sets range state which is what determines datepicker logic
   */
  const setFromToRange = useCallback(
    (dateStr: string, format: string) => {
      const strippedValue = stripDateBeforeAfter(dateStr)
      let fromRange: Date | null = null
      let toRange: Date | null = null
      const rangeQuery = parseRangeQuery(dateStr)
      const validDate = isValidDate(strippedValue, format)

      if (rangeQuery.from && rangeQuery.to) {
        fromRange = convertToNoonDate(rangeQuery.from, format)
        toRange = convertToNoonDate(rangeQuery.to, format)
      } else if (validDate) {
        fromRange = convertToNoonDate(strippedValue, format)
        toRange = convertToNoonDate(strippedValue, format)
      }

      setRange({ from: fromRange, to: toRange })
    },
    [convertToNoonDate, isValidDate]
  )

  /**
   * Set the before/after and range dates, either from user input or user
   * interaction with the datepicker.
   */
  const setDates = useCallback(
    (newBeforeAfterRange: string, utcDate: moment.Moment): DateRange => {
      const utcDateObj = utcDate.toDate()
      setBeforeAfterRange(newBeforeAfterRange)
      setBefore(newBeforeAfterRange === '<' ? utcDateObj : null)
      setAfter(newBeforeAfterRange === '>' ? utcDateObj : null)

      const newRange: DateRange = {
        from: newBeforeAfterRange === '>' ? utcDateObj : null,
        to: newBeforeAfterRange === '<' ? utcDateObj : null,
      }
      setRange(newRange)

      // Schedule updateClause after state is set
      setTimeout(() => {
        const formattedVal =
          newBeforeAfterRange + moment(utcDate).format(UI_DATE_FORMAT)
        updateClause({ target: { value: findDatepickerQuery(formattedVal) } })
      }, 0)

      return newRange
    },
    [findDatepickerQuery, updateClause]
  )

  const handleBeforeAfterRangeChange = useCallback(
    (e: { target: { value: string } }) => {
      const value = e.target.value
      if (allDatesAfter(value)) setBeforeAfterRange('>')
      else if (allDatesBefore(value)) setBeforeAfterRange('<')
      else setBeforeAfterRange('')
      onInputChange(e)
    },
    [onInputChange]
  )

  const submitFromEventValue = useCallback(
    (e: { target: { value: string } }) => {
      const userQuery = normalizeDate(e.target.value)
      const strippedValue = stripDateBeforeAfter(userQuery)
      const validDate = isValidDate(strippedValue, UI_DATE_FORMAT)

      if (validDate && allDatesBefore(userQuery)) {
        setDates('<', moment(strippedValue, UI_DATE_FORMAT).utc())
      } else if (validDate && allDatesAfter(userQuery)) {
        setDates('>', moment(strippedValue, UI_DATE_FORMAT).utc())
      } else if ((!isRangeQuery(userQuery) && !validDate) || userQuery === '') {
        resetSelectedDate()
      } else {
        resetBeforeAfter()
        setFromToRange(userQuery, UI_DATE_FORMAT)
      }

      if (validDate || isRangeQuery(userQuery)) {
        updateClauseInternal(userQuery, () =>
          submit(e as unknown as React.KeyboardEvent)
        )
      }
    },
    [
      isValidDate,
      resetBeforeAfter,
      resetSelectedDate,
      setDates,
      setFromToRange,
      submit,
      updateClauseInternal,
    ]
  )

  /**
   * Check if no days clicked yet OR start date is clicked with range selected
   * OR end date is clicked with range selected
   */
  const isInitialDate = useCallback(
    (from: number | null, to: number | null, utc: number) =>
      (!from && !to) ||
      (from === utc && to !== utc) ||
      (from !== utc && to === utc),
    []
  )

  const differentDatesClicked = useCallback(
    (
      currentBeforeAfterRange: string,
      utcDateTime: number,
      to: Date | null,
      toTime: number | null,
      adjustedRange: DateRange,
      strippedValue: string
    ): DateRange => {
      let rangeToUse = adjustedRange
      // Need this extra logic to override react day picker's functionality
      if (currentBeforeAfterRange === '<' && toTime && utcDateTime > toTime) {
        rangeToUse = { from: to, to: null }
      }
      const updatedRange = DateUtils.addDayToRange(
        new Date(strippedValue),
        rangeToUse
      ) as DateRange
      updateClauseInternal(
        `${moment(updatedRange.from).format(UI_DATE_FORMAT)} - ${moment(
          updatedRange.to
        ).format(UI_DATE_FORMAT)}`
      )

      return updatedRange
    },
    [updateClauseInternal]
  )

  /**
   * Keyup and click have different input args in DayPicker so we treat them differently.
   */
  const onDayClick = useCallback(
    (val: Date) => {
      const strippedValue = stripDateBeforeAfter(
        moment(val).format(UI_DATE_FORMAT)
      )
      if (isValidDate(strippedValue, UI_DATE_FORMAT)) {
        const utcDate = moment(strippedValue).utc()
        const { from, to } = range

        // Convert times to check equality
        const utcDateTime = utcDate.toDate()
          ? utcDate.toDate().getTime()
          : (utcDate.toDate() as unknown as number)
        const fromTime = from ? from.getTime() : null
        const toTime = to ? to.getTime() : null

        let newRange: DateRange

        if (
          isInitialDate(fromTime, toTime, utcDateTime) &&
          beforeAfterRange === ''
        ) {
          updateClauseInternal(utcDate)
          newRange = { from: utcDate.toDate(), to: utcDate.toDate() }
        } else if (
          fromTime === toTime &&
          fromTime === utcDateTime &&
          beforeAfterRange === ''
        ) {
          newRange = setDates('<', utcDate)
        } else if (
          !fromTime &&
          toTime === utcDateTime &&
          beforeAfterRange === '<'
        ) {
          newRange = setDates('>', utcDate)
          // Reset selected date so no dates are selected
        } else if (
          fromTime === utcDateTime &&
          !toTime &&
          beforeAfterRange === '>'
        ) {
          updateClauseInternal('')
          resetBeforeAfter()
          newRange = { from: null, to: null }
        } else {
          resetBeforeAfter()
          newRange = differentDatesClicked(
            beforeAfterRange,
            utcDateTime,
            to,
            toTime,
            range,
            strippedValue
          )
        }

        setRange(newRange)
      }
    },
    [
      beforeAfterRange,
      differentDatesClicked,
      isInitialDate,
      isValidDate,
      range,
      resetBeforeAfter,
      setDates,
      updateClauseInternal,
    ]
  )

  /**
   * This is specifically for search icon click. The event target is the icon.
   * We cannot use it to submit the event value. We have to use the daypicker ref
   * to submit the value instead of our searchVal because the search val only has
   * the last valid date rather than what is in the input box.
   */
  const submitFromSearchValue = useCallback(() => {
    if (findDatepickerQuery(searchRef.state.value).length) {
      submit({ type: 'click' })
    }
  }, [findDatepickerQuery, searchRef, submit])

  const Overlay: React.FC<OverlayProps> = useCallback(
    ({ onBlur, onFocus, children, classNames, ...props }) => (
      <div
        className={classNames.overlayWrapper}
        onBlur={onBlur}
        onFocus={onFocus}
        {...props}
      >
        <div className={cx('datepicker-header')}>
          <a onClick={onClear}>Back</a>
        </div>
        {children}
        <div className={cx('datepicker-tooltip')}>
          <PopupTooltip
            on='hover'
            position='bottom right'
            offset={12}
            horizontalOffset={0}
            verticalOffset={0}
            trigger={
              <Icon
                color='grayMedium'
                iconStyle='regular'
                name='info-circle'
                fitted
              />
            }
            content={DATEPICKER_TOOLTIP}
          />
        </div>
      </div>
    ),
    [onClear]
  ) as React.FC<OverlayProps>

  // componentDidMount equivalent - check if date input exists already to highlight calendar
  useEffect(() => {
    const handle = _.defer(() => {
      if (searchValue) {
        submitFromEventValue({
          target: {
            value: searchValue,
          },
        })
      }
    })
    return () => clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // UNSAFE_componentWillReceiveProps equivalent
  useEffect(() => {
    const prevSearchValue = prevSearchValueRef.current
    if (searchValue !== prevSearchValue) {
      const mockedEvent = { target: { value: searchValue } }
      handleBeforeAfterRangeChange(mockedEvent)
    }

    // Clear selected date(s) if user clears all search filters and grid re-renders
    if (searchValue === '' && prevSearchValue !== '') {
      resetSelectedDate()
    }

    prevSearchValueRef.current = searchValue
  }, [searchValue, handleBeforeAfterRangeChange, resetSelectedDate])

  const { from, to } = range
  const modifiers: Record<string, any> = {
    start: from,
    end: to,
    beforeAfter: [{ after }, { before }],
  }

  /**
   * Here we are rendering a div, and making it look like a semantic input.
   * Rendering the DayPickerInput inside of Semantic's Input component causes
   * problems with the search Icon, and general styling of the component. We pass
   * in an Overlay component to render the `before` `on` `after` button group as well.
   */
  return (
    <div
      className={`${cx('search-input')} ui input`}
      data-elm-id='search_input'
    >
      <DayPickerInput
        ref={getRef}
        value={searchValue}
        overlayComponent={Overlay}
        dayPickerProps={{
          numberOfMonths: 2,
          className: 'Selectable',
          selectedDays: [from, { from, to }] as any,
          modifiers,
          onDayClick,
        }}
        inputProps={{
          onKeyUp: submitFromEventValue,
          onChange: handleBeforeAfterRangeChange,
        }}
        format={UI_DATE_FORMAT}
        formatDate={formatDate}
        keepFocus
        hideOnDayClick={false}
        parseDate={MomentLocaleUtils.parseDate}
        placeholder={UI_DATE_FORMAT_RANGE}
        classNames={{
          overlay: '',
          overlayWrapper: cx('datepicker-overlay-wrapper'),
          container: cx('datepicker-container'),
        }}
      />
      <Icon
        name='search'
        color='info'
        link
        onClick={submitFromSearchValue}
        data-elm-id='search_icon'
        className={cx('search-icon', 'icon-date-picker-hack')}
      />
    </div>
  )
}

export default DatePicker
export { parseRangeQuery, isRangeQuery, stripDateBeforeAfter, normalizeDate }
